import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CASE_SEVERITIES, CASE_STATUSES } from '@hospasense/contracts';
import type { SupportCase } from '@hospasense/contracts';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useOrganisations } from '../lib/queries.js';
import { useCaseMutation, useCases } from '../lib/queries-s2.js';
import { formatDateTime, relativeTime, titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  PermissionDeniedState,
  SearchableSelect,
  Select,
  Skeleton,
  StatusPill,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const SEVERITY_TONES = { s1: 'error', s2: 'warning', s3: 'info', s4: 'neutral' } as const;
export const CASE_STATUS_TONES = {
  new: 'error',
  acknowledged: 'warning',
  in_progress: 'info',
  waiting_customer: 'neutral',
  resolved: 'success',
  closed: 'neutral',
} as const;

/** Support Queue: SLA clocks, severity filters, breach surfacing. */
export function SupportPage() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const allowed = hasPermission('case.read');
  const query = useCases(params, allowed);

  const setParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const orgs = useOrganisations(new URLSearchParams('pageSize=100&sortBy=displayName&sortDir=asc'));

  if (!allowed) return <PermissionDeniedState what="the Support Queue" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Queue"
        subtitle="Severity-driven SLA clocks; commitments override defaults; clinical content is prohibited"
        breadcrumbs={<Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Support' }]} />}
        actions={hasPermission('case.manage') ? <NewCaseButton /> : undefined}
      />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4">
        <div className="w-64">
          <SearchableSelect
            label="Filter by Client"
            placeholder="All organisations"
            value={params.get('organisationId') ?? ''}
            onChange={(val) => setParam('organisationId', val)}
            options={[
              { value: '', label: 'All organisations' },
              ...(orgs.data?.items ?? []).map((o) => ({
                value: o.id,
                label: o.displayName,
                subLabel: o.jurisdiction,
                badge: o.lifecycleState,
              })),
            ]}
          />
        </div>
        <Select
          label="Status"
          value={params.get('status') ?? ''}
          onChange={(e) => setParam('status', e.target.value)}
        >
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {titleCase(s)}
            </option>
          ))}
        </Select>
        <Select
          label="Severity"
          value={params.get('severity') ?? ''}
          onChange={(e) => setParam('severity', e.target.value)}
        >
          <option value="">All severities</option>
          {CASE_SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s.toUpperCase()}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 pb-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={params.get('open') === 'true'}
            onChange={(e) => setParam('open', e.target.checked ? 'true' : '')}
            className="size-4 accent-(--primary)"
          />
          Open only
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={params.get('breached') === 'true'}
            onChange={(e) => setParam('breached', e.target.checked ? 'true' : '')}
            className="size-4 accent-(--primary)"
          />
          SLA breached
        </label>
      </div>

      {query.isLoading ? (
        <TableContainer>
          <Skeleton rows={6} />
        </TableContainer>
      ) : query.isError || !query.data ? (
        <ErrorState
          description="The support queue could not be loaded."
          onRetry={() => void query.refetch()}
        />
      ) : query.data.items.length === 0 ? (
        <TableContainer>
          <EmptyState
            title="No cases match"
            description="Support cases matching the filters would appear here."
          />
        </TableContainer>
      ) : (
        <TableContainer>
          <Table aria-label="Support cases">
            <THead>
              <tr>
                <TH>Case</TH>
                <TH>Organisation</TH>
                <TH>Severity</TH>
                <TH>Status</TH>
                <TH>Assigned</TH>
                <TH>SLA</TH>
                <TH>Opened</TH>
              </tr>
            </THead>
            <TBody>
              {query.data.items.map((c) => (
                <TR key={c.id}>
                  <TD>
                    <Link
                      to={`/support/${c.id}`}
                      className="font-medium text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring rounded-sm focus-visible:outline-none"
                    >
                      #{c.caseNumber} {c.title}
                    </Link>
                  </TD>
                  <TD>{c.organisationName}</TD>
                  <TD>
                    <Badge tone={SEVERITY_TONES[c.severity]}>{c.severity.toUpperCase()}</Badge>
                  </TD>
                  <TD>
                    <StatusPill tone={CASE_STATUS_TONES[c.status]} label={titleCase(c.status)} />
                  </TD>
                  <TD>
                    {c.assignedToDisplayName ?? <span className="text-warning">Unassigned</span>}
                  </TD>
                  <TD>
                    <SlaCell c={c} />
                  </TD>
                  <TD>
                    <span title={formatDateTime(c.openedAt)}>{relativeTime(c.openedAt)}</span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}

export function SlaCell({ c }: { c: SupportCase }) {
  if (c.status === 'closed' || c.status === 'resolved') {
    return c.sla.resolveBreached ? (
      <Badge tone="error">Resolved late</Badge>
    ) : (
      <Badge tone="success">Met</Badge>
    );
  }
  if (c.sla.acknowledgeBreached || c.sla.resolveBreached) {
    return (
      <Badge tone="error">{c.sla.acknowledgeBreached ? 'Ack breached' : 'Resolve breached'}</Badge>
    );
  }
  return (
    <span
      className="text-xs text-text-muted"
      title={`Acknowledge by ${formatDateTime(c.sla.acknowledgeBy)}`}
    >
      ack {relativeTime(c.sla.acknowledgeBy).replace(' ago', '')}
      {c.sla.source === 'commitment' && ' · SLA: commitment'}
    </span>
  );
}

function NewCaseButton() {
  const [open, setOpen] = useState(false);
  const orgs = useOrganisations(new URLSearchParams('pageSize=100&sortBy=displayName&sortDir=asc'));
  const { create } = useCaseMutation(undefined);
  const toast = useToast();
  const [form, setForm] = useState({
    organisationId: '',
    title: '',
    description: '',
    severity: 's3',
  });

  return (
    <>
      <Button onClick={() => setOpen(true)}>New case</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Open a support case"
        wide
        footer={
          <Button
            loading={create.isPending}
            disabled={
              !form.organisationId ||
              form.title.trim().length < 5 ||
              form.description.trim().length < 5
            }
            onClick={() =>
              void create
                .mutateAsync(form as never)
                .then(() => {
                  toast.success('Case opened');
                  setOpen(false);
                  setForm({ organisationId: '', title: '', description: '', severity: 's3' });
                })
                .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Rejected'))
            }
          >
            Open case
          </Button>
        }
      >
        <div className="space-y-3">
          <SearchableSelect
            label="Organisation"
            required
            placeholder="Search and choose client organisation…"
            value={form.organisationId}
            onChange={(val) => setForm((f) => ({ ...f, organisationId: val }))}
            options={(orgs.data?.items ?? []).map((o) => ({
              value: o.id,
              label: o.displayName,
              subLabel: `${o.legalName} · ${o.jurisdiction}`,
              badge: o.lifecycleState,
            }))}
          />
          <Input
            label="Title"
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <Textarea
            label="Description"
            required
            hint="Operational detail only — patient or clinical content is rejected."
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Select
            label="Severity"
            value={form.severity}
            onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
          >
            {CASE_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s.toUpperCase()}
              </option>
            ))}
          </Select>
        </div>
      </Modal>
    </>
  );
}
